import { matchPath } from "react-router";

import PATHS, { PERSON_TAB, TEAM_TAB } from "src/consts/urlPaths";
import { BAMBOOHR_OAUTH_CALLBACK_PATH } from "src/integrationTypes/components/bambooHROAuth";

/**
 * How a page behaves while a design option is selected.
 * - supported: reads and writes the option.
 * - readOnly: shows baseline data; non-option-aware writes are disabled.
 * - blocked: replaced by a notice with a way back to Baseline.
 */
export type OptionPageSupport = "supported" | "readOnly" | "blocked";

type UnsupportedSupport = Exclude<OptionPageSupport, "supported">;

/**
 * A route whose support depends on the `:tabType` param. A path with no tab
 * (`/teams/:id`) is classified as its `defaultTab`, which is where the app
 * redirects it.
 */
type TabbedSupport = {
  kind: "byTab";
  defaultTab: string;
  supportedTabs: readonly string[];
  readOnlyTabs: readonly string[];
  otherwise: UnsupportedSupport;
};

type RouteSupport = OptionPageSupport | TabbedSupport;

const byTab = (
  defaultTab: string,
  supportedTabs: readonly string[],
  otherwise: UnsupportedSupport,
  readOnlyTabs: readonly string[] = []
): TabbedSupport => ({
  kind: "byTab",
  defaultTab,
  supportedTabs,
  readOnlyTabs,
  otherwise,
});

const ROUTE_PATHS = {
  ...PATHS,
  BAMBOOHR_OAUTH_CALLBACK: BAMBOOHR_OAUTH_CALLBACK_PATH,
};

export type OptionRouteKey = keyof typeof ROUTE_PATHS;

const TEAM_TABS = byTab(
  TEAM_TAB.DETAILS,
  [
    TEAM_TAB.DETAILS,
    TEAM_TAB.ORG_CHART,
    TEAM_TAB.MEMBERSHIPS,
    TEAM_TAB.ASSOCIATIONS,
    TEAM_TAB.STRATEGY,
    TEAM_TAB.PRINCIPLES,
  ],
  // Forecast, planner, team builder and allocation tabs write demand data
  // with no option twin, so they are blocked rather than read-only.
  "blocked",
  [
    // Metrics has no page of its own and nothing on it writes.
    TEAM_TAB.METRICS,
    // Changelog diffs and History comments are read from the baseline
    // timeline. The API has no option-scoped changelog or history.
    TEAM_TAB.CHANGELOG,
    TEAM_TAB.HISTORY,
    // Insights lists the option's child teams, but every metric on it comes
    // from baseline aggregations, so the page as a whole is baseline data.
    TEAM_TAB.INSIGHTS,
  ]
);

const PERSON_TABS_EXCEPT_HISTORY = Object.values(PERSON_TAB).filter(
  (tab) => tab !== PERSON_TAB.HISTORY
);

// History comments are read from the baseline timeline. The API has no
// option-scoped person history, same as team History.
const PERSON_HISTORY_TABS = [PERSON_TAB.HISTORY];

const PERSON_TABS = byTab(
  PERSON_TAB.DETAILS,
  PERSON_TABS_EXCEPT_HISTORY,
  "readOnly",
  PERSON_HISTORY_TABS
);
const USER_HOME_TABS = byTab(
  PERSON_TAB.USER_DASHBOARD,
  PERSON_TABS_EXCEPT_HISTORY,
  "readOnly",
  PERSON_HISTORY_TABS
);

/**
 * Every route in `getRoutes` (plus the redirect-only paths in `urlPaths`).
 * `Record` makes a new path a type error until it is classified here.
 * No page gains option support until the next architecture iteration, so
 * new routes default to `readOnly` or `blocked`, not `supported`.
 */
export const OPTION_PAGE_SUPPORT: Record<OptionRouteKey, RouteSupport> = {
  // Option-aware today.
  TEAM_DETAILS: TEAM_TABS,
  TEAM_DETAILS_BASE_PATH: TEAM_TABS,
  PERSON_DETAILS: PERSON_TABS,
  PERSON_DETAILS_BASE_PATH: PERSON_TABS,
  PERSON_DETAILS_FROM_DIRECTORY: PERSON_TABS,
  USER_HOME: USER_HOME_TABS,
  DIRECTORY_USER_HOME: USER_HOME_TABS,
  DIRECTORY_USER_HOME_BASE_PATH: USER_HOME_TABS,
  USER_HOME_BASE_PATH: USER_HOME_TABS,
  // Renders the option-aware org chart and only writes user view preferences.
  TEAM_CANVAS: "supported",

  // Configuration, imports, admin and approval surfaces.
  DATASOURCE: "blocked",
  WORKSPACE_CONFIGURATION: "blocked",
  WORKSPACE_SETTINGS: "blocked",
  WORKSPACE_SETTINGS_ROOT: "blocked",
  ADMIN: "blocked",
  ADMIN_DASHBOARD: "blocked",
  BAMBOOHR_OAUTH_CALLBACK: "blocked",
  INTERNAL_DASHBOARD: "blocked",
  INTERNAL_DASHBOARD_GENERAL: "blocked",
  ORG_DESIGN_SETTINGS: "blocked",
  DESIGN_EDIT: "blocked",
  WORKSPACES: "blocked",
  CREATE_WORKSPACE: "blocked",
  WORKFLOWS: "blocked",
  WORKFLOW_BUILDER: "blocked",
  WORKFLOW_RUNS: "blocked",
  WORKFLOW_RUN_DETAILS: "blocked",
  WORKFORCE_INBOX: "blocked",
  WORKFORCE_ROOT: "blocked",
  WORKFORCE: "blocked",
  WORKFORCE_PLANNING_GUARDRAILS_ADMIN: "blocked",

  // Everything else shows baseline data.
  NEW_USER_LANDING: "readOnly",
  INSIGHTS: "readOnly",
  SUMMARY_LEGACY: "readOnly",
  SEARCH: "readOnly",
  PEOPLE: "readOnly",
  TAG_EXPLORE: "readOnly",
  TEAM_EXPLORE: "readOnly",
  GOALS_EXPLORE: "readOnly",
  EXPLORE: "readOnly",
  VISUALISATION_WITH_ID: "readOnly",
  ORG_DESIGN_HOME: "readOnly",
  TEAMS: "readOnly",
  PERSON_DETAILS_EMAIL_LINK: "readOnly",
  // Reads baseline, so its option-aware tag writers are disabled.
  TAG_DETAILS: "readOnly",
  POSITION_DETAILS: "readOnly",
  POSITION_DETAILS_BASE_PATH: "readOnly",
  WORKFORCE_PLANNING_DASHBOARD: "readOnly",
  WORKFORCE_PLANNING_GUARDRAILS: "readOnly",
  WORKFORCE_PLANNING_GUARDRAILS_COMMAND_TOWER: "readOnly",
  WORKFORCE_PLANNING_GUARDRAILS_COMMAND_CENTRE_LEGACY: "readOnly",
  LOGOUT: "readOnly",
  REPORTS: "readOnly",
  USER_REPORT: "readOnly",
  API_DOCS: "readOnly",
  LOGIN_HISTORY_PAGE: "readOnly",
  AI_DASHBOARDS: "readOnly",
  AI_DASHBOARDS_V2: "readOnly",
  AI_DASHBOARDS_LIBRARY: "readOnly",
  SLIDES: "readOnly",
  SLIDE_DECK: "readOnly",
  DESIGNS: "readOnly",
  DESIGN_DETAIL: "readOnly",
  APP_HOME: "readOnly",
};

const resolveRouteSupport = (
  support: RouteSupport,
  tabType: string | undefined
): OptionPageSupport => {
  if (typeof support === "string") {
    return support;
  }
  const tab = tabType ?? support.defaultTab;
  if (support.supportedTabs.includes(tab)) {
    return "supported";
  }
  return support.readOnlyTabs.includes(tab) ? "readOnly" : support.otherwise;
};

const ROUTE_KEYS = Object.keys(ROUTE_PATHS) as OptionRouteKey[];

/**
 * Classifies a URL pathname. Walks the routes in `urlPaths` order, which is
 * how `matchPaths` disambiguates overlapping patterns. Unknown paths are
 * `readOnly`: the safe default is to show baseline data and disable edits.
 */
export const getOptionPageSupport = (pathname: string): OptionPageSupport => {
  // The router ignores a trailing slash, so `/teams/t1/orgchart/` is the
  // same page as `/teams/t1/orgchart`.
  const normalised =
    pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  for (const key of ROUTE_KEYS) {
    const path = ROUTE_PATHS[key];
    if (typeof path !== "string") {
      continue;
    }
    const match = matchPath<{ tabType?: string }>(normalised, {
      path,
      exact: true,
    });
    if (match) {
      return resolveRouteSupport(
        OPTION_PAGE_SUPPORT[key],
        match.params.tabType
      );
    }
  }
  return "readOnly";
};

/** Route patterns the registry covers, for tests that check `getRoutes`. */
export const OPTION_ROUTE_PATHS: Readonly<
  Record<OptionRouteKey, string | undefined>
> = ROUTE_PATHS;
